import { useState, useEffect } from "react";
import { RefreshCw, Search, Loader2, Power, PowerOff, AlertTriangle, Coins } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileCards, DataCard, EmptyCard } from "@/components/DataCards";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminApps() {
  const [apps, setApps] = useState([]);
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [bulking, setBulking] = useState(false);
  const [syncingCredits, setSyncingCredits] = useState(false);
  const [edit, setEdit] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkCredit, setBulkCredit] = useState("");
  const [applying, setApplying] = useState(false);
  const [onlyUnpriced, setOnlyUnpriced] = useState(false);

  const load = () => api.get(`/apps/admin${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => { setApps(data); setSelected(new Set()); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const isUnpriced = (a) => a.price == null || (Number(a.price) === 20 && Number(a.credit_cost) === 1);
  const syncCredits = async () => {
    setSyncingCredits(true);
    try { const { data } = await api.post("/apps/sync-credits"); toast.success(`${data.updated} apps com créditos do Ative`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSyncingCredits(false); }
  };
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const applyBulkPrice = async () => {
    const payload = { ids: [...selected] };
    if (bulkPrice !== "") payload.price = Number(bulkPrice);
    if (bulkCredit !== "") payload.credit_cost = Number(bulkCredit);
    if (payload.price == null && payload.credit_cost == null) return toast.error("Informe preço e/ou créditos");
    setApplying(true);
    try {
      const { data } = await api.post("/apps/bulk-price", payload);
      toast.success(`${data.modified} app(s) atualizados`);
      setBulkPrice(""); setBulkCredit(""); setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e)); } finally { setApplying(false); }
  };

  const bulkActive = async (active) => {
    const scope = q ? `os apps que correspondem a "${q}"` : "TODOS os aplicativos";
    if (!window.confirm(`Deseja ${active ? "ATIVAR" : "DESATIVAR"} ${scope}?`)) return;
    setBulking(true);
    try {
      const { data } = await api.post("/apps/bulk-active", { active, q: q || null });
      toast.success(`${data.modified} app(s) ${active ? "ativados" : "desativados"}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulking(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/apps/sync");
      toast.success(`Sincronizado: ${data.imported} novos, ${data.updated} atualizados`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
  };

  const toggleActive = async (app) => {
    try {
      await api.patch(`/apps/${app.id}`, { active: !app.active });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/apps/${edit.id}`, {
        name: edit.name, description: edit.description, logo: edit.logo,
        category: edit.category, credit_cost: Number(edit.credit_cost),
        price: edit.price === "" || edit.price == null ? null : Number(edit.price),
        order: Number(edit.order),
      });
      toast.success("Aplicativo atualizado");
      setEdit(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const displayed = onlyUnpriced ? apps.filter(isUnpriced) : apps;
  const unpricedCount = apps.filter(isUnpriced).length;
  const allSelected = displayed.length > 0 && displayed.every((a) => selected.has(a.id));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) displayed.forEach((a) => n.delete(a.id));
    else displayed.forEach((a) => n.add(a.id));
    return n;
  });

  return (
    <div className="space-y-6" data-testid="admin-apps">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Aplicativos</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => bulkActive(true)} disabled={bulking} className="gap-2" data-testid="apps-activate-all-btn">
            <Power className="h-4 w-4" /> Ativar todos
          </Button>
          <Button variant="outline" onClick={() => bulkActive(false)} disabled={bulking} className="gap-2" data-testid="apps-deactivate-all-btn">
            <PowerOff className="h-4 w-4" /> Desativar todos
          </Button>
          <Button variant="outline" onClick={syncCredits} disabled={syncingCredits} className="gap-2" data-testid="apps-sync-credits-btn">
            {syncingCredits ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />} Sincronizar créditos
          </Button>
          <Button onClick={sync} disabled={syncing} className="gap-2" data-testid="apps-sync-btn">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sincronizar com Ative Apps
          </Button>
        </div>
      </div>

      <div className="relative w-full sm:w-80">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar aplicativo..." className="pl-9" data-testid="apps-search-input" />
      </div>

      {unpricedCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="unpriced-banner">
          <span className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" /> <b>{unpricedCount}</b> app(s) ainda no preço padrão (R$ 20,00 / 1 cr.)
          </span>
          <Button variant="outline" size="sm" onClick={() => setOnlyUnpriced((v) => !v)} data-testid="toggle-unpriced-btn">
            {onlyUnpriced ? "Mostrar todos" : "Mostrar só sem preço"}
          </Button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-primary/40 bg-primary/5 p-3" data-testid="bulk-price-bar">
          <span className="text-sm font-medium">{selected.size} selecionado(s)</span>
          <div className="space-y-1">
            <Label className="text-xs">Preço (R$)</Label>
            <Input type="number" step="0.01" min="0" value={bulkPrice} onChange={(e) => setBulkPrice(e.target.value)} placeholder="Ex: 12.00" className="h-9 w-32" data-testid="bulk-price-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Custo (cr.)</Label>
            <Input type="number" step="0.1" min="0" value={bulkCredit} onChange={(e) => setBulkCredit(e.target.value)} placeholder="Ex: 0.9" className="h-9 w-28" data-testid="bulk-credit-input" />
          </div>
          <Button onClick={applyBulkPrice} disabled={applying} className="gap-2" data-testid="bulk-price-apply-btn">
            {applying && <Loader2 className="h-4 w-4 animate-spin" />} Aplicar aos {selected.size}
          </Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn">Limpar</Button>
        </div>
      )}

      <MobileCards testid="apps-cards">
        {displayed.length === 0 && <EmptyCard>Nenhum app.</EmptyCard>}
        {displayed.map((a) => (
          <DataCard
            key={a.id}
            testid={`admin-app-card-${a.id}`}
            leading={<Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggleSel(a.id)} data-testid={`app-select-m-${a.id}`} />}
            title={a.name}
            subtitle={a.category}
            badge={<Switch checked={a.active} onCheckedChange={() => toggleActive(a)} data-testid={`app-active-m-${a.id}`} />}
            rows={[
              { label: "Preço", value: <span className="font-num">{a.price != null ? `R$ ${Number(a.price).toFixed(2)}` : "—"}</span> },
              { label: "Custo (cr.)", value: <span className="font-num">{a.credit_cost}</span> },
              {
                label: "Requer", full: true,
                value: (
                  <span className="flex flex-wrap gap-1">
                    {a.needs_mac_key && <Badge variant="secondary" className="text-[10px]">MAC+KEY</Badge>}
                    {a.needs_device_id && <Badge variant="secondary" className="text-[10px]">Device ID</Badge>}
                    {!a.needs_mac_key && !a.needs_device_id && <Badge variant="secondary" className="text-[10px]">MAC</Badge>}
                  </span>
                ),
              },
            ]}
            actions={<Button variant="outline" size="sm" className="flex-1" onClick={() => setEdit({ ...a })} data-testid={`app-edit-m-${a.id}`}>Editar</Button>}
          />
        ))}
      </MobileCards>

      <div className="hidden rounded-lg border border-border bg-card md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="bulk-select-all" /></TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Preço (R$)</TableHead>
              <TableHead>Custo (cr.)</TableHead>
              <TableHead>Requer</TableHead>
              <TableHead>Ativo</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayed.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Nenhum app.</TableCell></TableRow>}
            {displayed.map((a) => (
              <TableRow key={a.id} data-testid={`admin-app-${a.id}`} data-state={selected.has(a.id) ? "selected" : undefined}>
                <TableCell><Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggleSel(a.id)} data-testid={`app-select-${a.id}`} /></TableCell>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell className="text-muted-foreground">{a.category}</TableCell>
                <TableCell className="font-num" data-testid={`app-price-${a.id}`}>{a.price != null ? `R$ ${Number(a.price).toFixed(2)}` : "—"}</TableCell>
                <TableCell className="font-num">{a.credit_cost}</TableCell>
                <TableCell className="space-x-1">
                  {a.needs_mac_key && <Badge variant="secondary" className="text-[10px]">MAC+KEY</Badge>}
                  {a.needs_device_id && <Badge variant="secondary" className="text-[10px]">Device ID</Badge>}
                  {!a.needs_mac_key && !a.needs_device_id && <Badge variant="secondary" className="text-[10px]">MAC</Badge>}
                </TableCell>
                <TableCell><Switch checked={a.active} onCheckedChange={() => toggleActive(a)} data-testid={`app-active-${a.id}`} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => setEdit({ ...a })} data-testid={`app-edit-${a.id}`}>Editar</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="app-edit-dialog">
          <DialogHeader><DialogTitle>Editar aplicativo</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="space-y-2"><Label>Nome</Label><Input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="app-edit-name" /></div>
              <div className="space-y-2"><Label>Descrição</Label><Input value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="app-edit-desc" /></div>
              <div className="space-y-2"><Label>Logo (URL)</Label><Input value={edit.logo || ""} onChange={(e) => setEdit({ ...edit, logo: e.target.value })} data-testid="app-edit-logo" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Categoria</Label><Input value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} data-testid="app-edit-category" /></div>
                <div className="space-y-2"><Label>Preço (R$)</Label><Input type="number" step="0.01" min="0" value={edit.price ?? ""} onChange={(e) => setEdit({ ...edit, price: e.target.value })} placeholder="Ex: 20.00" data-testid="app-edit-price" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Custo (créditos)</Label><Input type="number" min="0" value={edit.credit_cost} onChange={(e) => setEdit({ ...edit, credit_cost: e.target.value })} data-testid="app-edit-cost" /></div>
                <div className="space-y-2"><Label>Ordem</Label><Input type="number" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} data-testid="app-edit-order" /></div>
              </div>
              <p className="text-xs text-muted-foreground">O <b>Preço (R$)</b> aparece no site/bot para pagamento via Pix. O <b>Custo (créditos)</b> é quanto debita da carteira ao ativar por créditos.</p>
              <Button className="w-full" onClick={saveEdit} data-testid="app-edit-save">Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
